import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import TotalBalanceCard from './cards/TotalBalanceCard';
import MonthlyIncomeCard from './cards/MonthlyIncomeCard';
import MonthlyExpensesCard from './cards/MonthlyExpensesCard';
import SpendingTrendChart from './charts/SpendingTrendChart';
import SpendingByCategory from './charts/SpendingByCategory';
import TransactionTrendChart from './charts/TransactionTrendChart';
import TransactionByAccountChart from './charts/TransactionByAccountChart';
import SpendingByTypeChart from './charts/SpendingByTypeChart';
import IncomeVsSpendingChart from './charts/IncomeVsSpendingChart';
import CumulativeWealthChart from './charts/CumulativeWealthChart';
import MonthlyIncomeSpendingChart from './charts/MonthlyIncomeSpendingChart';
import RecentActivity from './RecentActivity';
import OnboardingChecklist from './OnboardingChecklist';
import { useDashboardData } from '../hooks/useDashboardData';
import { useDateRange } from '../hooks/useDateRange';
import { useComparisonData } from '../hooks/useComparisonData';
import dashboardService from '../services/dashboardService';
import DateRangePicker from '../../../components/shared/DateRangePicker';
import ComparisonToggle from '../../../components/shared/ComparisonToggle';
import ExportMenu from '../../../components/shared/ExportMenu';
import InsightsPanel from './InsightsPanel';
import { 
  ArrowPathIcon, 
  ShieldExclamationIcon, 
  ExclamationTriangleIcon, 
  ShareIcon,
  UserIcon,
  UsersIcon,
  ArrowsPointingOutIcon,
  ArrowsPointingInIcon
} from '@heroicons/react/24/outline';
import { toast } from 'react-hot-toast';
import { useAuthContext } from '../../auth/context/AuthContext';
import { useTheme } from '../../../context/ThemeContext';
import walletService from '../../wallet/services/walletService';
import PartnerConnectionModal from '../../../components/shared/PartnerConnectionModal';
import SharingManagementModal from '../../../components/shared/SharingManagementModal';
import SecurityNotice from '../../../components/shared/SecurityNotice';
import MobileViewSelector from '../../../components/shared/MobileViewSelector';


// Reference the API URL for error messages
const API_URL = `${import.meta.env.VITE_API_URL || 'http://localhost:3000'}/api`;

/**
 * Dashboard Component
 * 
 * Main dashboard view that displays financial overview and insights
 * Layout follows a grid system with responsive design
 * 
 * @returns {JSX.Element} Dashboard component
 */
const Dashboard = () => {
  const { themeColors } = useTheme();
  const [connectionError, setConnectionError] = useState(false);
  const [loadAttempts, setLoadAttempts] = useState(0);
  const [showPartnerModal, setShowPartnerModal] = useState(false);
  const [showSharingModal, setShowSharingModal] = useState(false);
  const [sentInvitations, setSentInvitations] = useState([]);
  const [receivedInvitations, setReceivedInvitations] = useState([]);
  
  // Add missing state variables
  const [invitationsLoading, setInvitationsLoading] = useState(false);
  const [actionInProgress, setActionInProgress] = useState(false);
  const [sendingInvitation, setSendingInvitation] = useState(false);
  const [invitationEmail, setInvitationEmail] = useState('');
  const [pendingInvitationEmail, setPendingInvitationEmail] = useState('');
  const [showAccessLevelModal, setShowAccessLevelModal] = useState(false);
  const [selectedAccessLevel, setSelectedAccessLevel] = useState('custom');
  const [disconnecting, setDisconnecting] = useState(false);
  const [showDisconnectConfirm, setShowDisconnectConfirm] = useState(false);
  const [showTransactionDetails, setShowTransactionDetails] = useState(true);
  const [dataView, setDataView] = useState('combined'); // 'personal', 'partner', 'combined'
  const [expandedChart, setExpandedChart] = useState(null); // Track which chart is expanded: 'category', 'account', 'type', or null
  
  const location = useLocation();
  
  // Debug: Log when dataView changes
  useEffect(() => {
    console.log('🔍 Dashboard: dataView state changed to:', dataView);
  }, [dataView]);
  
  // Date range state management - default to all time to show full transaction history
  const { dateRange, setDateRange, resetDateRange } = useDateRange('dashboard_dateRange', 'alltime');
  
  // Comparison mode state
  const [comparisonMode, setComparisonMode] = React.useState('none');
  const { comparisonData, isLoading: comparisonLoading, getComparisonMetrics } = useComparisonData(dateRange, comparisonMode);
  
  const { 
    acceptPartnerInvite, 
    user, 
    refreshUserData,
    generatePartnerInvite,
    getPartnerInvitations,
    cancelPartnerInvite,
    resendPartnerInvite,
    removePartner
  } = useAuthContext();
  
  const dashboardDataFromHook = useDashboardData(dataView);
  const {
    totalBalance,
    monthlyIncome,
    monthlyExpenses,
    spendingTrends,
    categoryBreakdown,
    transactionTrends,
    incomeTrends,
    recentActivity,
    isLoading,
    error,
    refresh,
    useDemo,
    lastUpdated,
    isDemo,
    incomeCount,
    accountCount
  } = dashboardDataFromHook;

  // Check for partner invite flag and token in URL or sessionStorage
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const hasPartnerInvite = params.get('partner-invite') === 'true';
    
    if (hasPartnerInvite) {
      // Check if there's a stored partner invite code
      const inviteCode = sessionStorage.getItem('partnerInviteCode');
      
      if (inviteCode) {
        console.log('🎟️ Found partner invitation code:', inviteCode);
        
        // Check if user already has a partner (partnership was established during registration)
        if (user?.hasPartner || user?.partnerId) {
          console.log('✅ User already has a partner, skipping invitation acceptance');
          // Clean up the session storage
          sessionStorage.removeItem('partnerInviteCode');
          toast.success('Welcome! You are now connected with your partner.');
          return;
        }
        
        // Auto-accept partner invitation only if user doesn't have a partner yet
        const handleInvite = async () => {
          try {
            await acceptPartnerInvite(inviteCode, true);
            toast.success('Partner invitation accepted successfully!');
            // Clean up the session storage
            sessionStorage.removeItem('partnerInviteCode');
          } catch (error) {
            console.error('Failed to accept partner invitation:', error);
            toast.error('Failed to accept partner invitation. Please try manually.');
            // Open the partner modal for manual handling
            setShowPartnerModal(true);
          }
        };
        
        handleInvite();
      } else {
        // No code but flag is present, just show the partner modal
        setShowPartnerModal(true);
      }
    }
  }, [location, acceptPartnerInvite, user]);

  // Log dashboard data for debugging
  useEffect(() => {
    console.log('Dashboard: Component rendered with data:', {
      totalBalance,
      monthlyIncome,
      monthlyExpenses,
      lastUpdated,
      isDemo,
      error
    });
    
    if (error && error.includes('Network Error')) {
      setConnectionError(true);
    } else {
      setConnectionError(false);
    }
  }, [totalBalance, monthlyIncome, monthlyExpenses, lastUpdated, isDemo, error]);

  const formatLastUpdated = (date) => {
    if (!date) return '';
    return new Date(date).toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const handleRefresh = async () => {
    try {
      console.log('Dashboard: Refreshing data with date range:', dateRange);
      await refresh(dateRange);
      toast.success('Dashboard refreshed');
    } catch (error) {
      console.error('Dashboard: Refresh error:', error);
      toast.error('Failed to refresh dashboard');
      
      // If refresh fails, try to load demo data
      if (!isDemo) {
        handleUseDemo();
      }
    }
  };

  // Handle date range change
  const handleDateRangeChange = (newDateRange) => {
    console.log('Dashboard: Date range changed:', newDateRange);
    setDateRange(newDateRange);
    // Refresh will be triggered automatically by useDashboardData when dateRange changes
  };

  const handleForceRefresh = () => {
    console.log('Dashboard: Force refreshing page...');
    // Add a timestamp to ensure a complete refresh
    window.location.href = window.location.pathname + '?t=' + new Date().getTime();
  };

  const handleUseDemo = async () => {
    try {
      console.log('Dashboard: Loading demo data...');
      await useDemo();
      toast.success('Using demo data for dashboard');
    } catch (error) {
      console.error('Dashboard: Demo data error:', error);
      toast.error('Failed to load demo data');
    }
  };

  // Handle opening sharing management modal
  const handleOpenSharingModal = async () => {
    // First refresh partnership status to ensure UI is current
    await checkPartnershipStatus();
    setShowSharingModal(true);
  };

  // Check and refresh partnership status
  const checkPartnershipStatus = async () => {
    try {
      // Refresh user data to get current partnership status
      await refreshUserData();
      console.log('✅ Partnership status refreshed');
    } catch (error) {
      console.error('Failed to refresh partnership status:', error);
    }
  };

  // Handle sending invitation
  const handleSendInvitation = async () => {
    if (!invitationEmail || !invitationEmail.includes('@')) {
      toast.error('Please enter a valid email address');
      return;
    }

    setPendingInvitationEmail(invitationEmail);
    setShowInvitationModal(false);
    setShowAccessLevelModal(true);
  };

  // Handle access level confirmation and send invitation
  const handleConfirmAccessLevel = async () => {
    if (!pendingInvitationEmail) {
      toast.error('Email address is required');
      return;
    }

    try {
      setSendingInvitation(true);
      
      console.log('🎯 Sending partner invitation with access level:', {
        email: pendingInvitationEmail,
        accessLevel: selectedAccessLevel
      });

      const result = await generatePartnerInvite(pendingInvitationEmail, selectedAccessLevel);
      
      if (result.success) {
        toast.success(`Partner invitation sent to ${pendingInvitationEmail}!`);
        setInvitationEmail('');
        setPendingInvitationEmail('');
        setShowAccessLevelModal(false);
        await loadInvitations();
      } else {
        toast.error(result.message || 'Failed to send invitation');
      }
    } catch (error) {
      console.error('Error sending invitation:', error);
      toast.error('Failed to send invitation: ' + error.message);
    } finally {
      setSendingInvitation(false);
    }
  };

  // Load invitations
  const loadInvitations = async () => {
    try {
      setInvitationsLoading(true);
      const invitations = await getPartnerInvitations();
      setSentInvitations(invitations.sent || []);
      setReceivedInvitations(invitations.received || []);
    } catch (error) {
      console.error('Error loading invitations:', error);
    } finally {
      setInvitationsLoading(false);
    }
  };

  // Handle canceling invitation
  const handleCancelInvitation = async (invitationId) => {
    try {
      await cancelPartnerInvite(invitationId);
      toast.success('Invitation cancelled');
      await loadInvitations();
    } catch (error) {
      console.error('Error canceling invitation:', error);
      toast.error('Failed to cancel invitation');
    }
  };

  // Handle resending invitation
  const handleResendInvitation = async (invitationId) => {
    try {
      await resendPartnerInvite(invitationId);
      toast.success('Invitation resent');
      await loadInvitations();
    } catch (error) {
      console.error('Error resending invitation:', error);
      toast.error('Failed to resend invitation');
    }
  };

  // Handle disconnecting partner
  const handleDisconnectPartner = async () => {
    try {
      setDisconnecting(true);
      await removePartner();
      toast.success('Partner disconnected successfully');
      setShowDisconnectConfirm(false);
      setShowSharingModal(false);
      await refreshUserData();
    } catch (error) {
      console.error('Error disconnecting partner:', error);
      toast.error('Failed to disconnect partner');
    } finally {
      setDisconnecting(false);
    }
  };

  // Handle manage sharing settings
  const handleManageSharingSettings = () => {
    // Close current modal and show sharing modal from PartnerInfoCard
    setShowSharingModal(false);
    // This could trigger a more detailed sharing settings modal
    toast.info('Use the Wallet page to manage individual account sharing');
  };

  // Handle view shared accounts
  const handleViewSharedAccounts = () => {
    setShowSharingModal(false);
    toast.info('Visit the Wallet page to view shared accounts');
  };

  // Handle advanced settings
  const handleAdvancedSettings = () => {
    // TODO: Implement advanced settings
    toast.info('Advanced settings feature coming soon!');
  };

  // Handle view activity log
  const handleViewActivityLog = () => {
    // TODO: Implement activity log functionality
    toast.info('Activity log feature coming soon!');
  };

  // Handle sync accounts
  const handleSyncAccounts = async () => {
    try {
      setActionInProgress(true);
      await refreshUserData();
      toast.success('Accounts synced successfully');
    } catch (error) {
      console.error('Error syncing accounts:', error);
      toast.error('Failed to sync accounts');
    } finally {
      setActionInProgress(false);
    }
  };

  // Handle transaction details toggle
  const handleTransactionDetailsToggle = async (enabled) => {
    try {
      setActionInProgress(true);
      setShowTransactionDetails(enabled);
      
      console.log('🔄 Toggling transaction details globally:', enabled);
      
      toast.success(enabled ? 'Transaction sharing enabled' : 'Transaction sharing disabled');
      
    } catch (error) {
      console.error('Failed to toggle transaction details:', error);
      toast.error('Failed to update transaction sharing settings');
      setShowTransactionDetails(!enabled);
    } finally {
      setActionInProgress(false);
    }
  };

  // Load invitations when modal opens
  useEffect(() => {
    if (showSharingModal) {
      loadInvitations();
    }
  }, [showSharingModal]);

  // Get partner name for display
  const partnerName = user?.partner?.firstName || user?.createdPartner?.firstName || 'Partner';
  const hasPartner = !!(user?.partner || user?.createdPartner || user?.partnerId);
  const partnerStatus = hasPartner ? 'connected' : 'disconnected';

  // Connection error banner
  if (connectionError) {
    return (
      <div className="p-4">
        <div className="bg-orange-50 border-l-4 border-orange-500 p-4 rounded shadow-md mb-6">
          <div className="flex items-center">
            <ShieldExclamationIcon className="h-8 w-8 text-orange-500 mr-4" />
            <div>
              <h3 className="text-lg font-semibold text-orange-700">Server Connection Issue</h3>
              <p className="text-orange-700 mb-2">
                Unable to connect to the server at {API_URL}. Please make sure the server is running on port 3000.
              </p>
              <div className="flex flex-col gap-2 mt-2">
                <p className="text-sm text-orange-600">
                  To start the server, open a terminal and run:
                  <code className="ml-2 px-2 py-1 bg-orange-100 rounded">cd server && npm run dev -- --port 3000</code>
                </p>
                <div className="flex gap-2">
                  <button 
                    onClick={handleRefresh}
                    className="bg-orange-500 hover:bg-orange-600 text-white px-3 py-1 rounded text-sm"
                  >
                    Try Again
                  </button>
                  <button 
                    onClick={handleUseDemo}
                    className="bg-white border border-orange-500 hover:bg-orange-50 text-orange-600 px-3 py-1 rounded text-sm"
                  >
                    Use Demo Data
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        {/* Still render the dashboard with demo data */}
        <DashboardContent 
          totalBalance={totalBalance} 
          monthlyIncome={monthlyIncome} 
          monthlyExpenses={monthlyExpenses}
          spendingTrends={spendingTrends}
          categoryBreakdown={categoryBreakdown}
          transactionTrends={transactionTrends}
          recentActivity={recentActivity}
          isLoading={isLoading}
          isDemo={true}
          lastUpdated={lastUpdated}
          onRefresh={handleRefresh}
          onForceRefresh={handleForceRefresh}
          onUseDemo={handleUseDemo}
          setShowPartnerModal={setShowPartnerModal}
          handleOpenSharingModal={handleOpenSharingModal}
          dateRange={dateRange}
          handleDateRangeChange={handleDateRangeChange}
          comparisonMode={comparisonMode}
          setComparisonMode={setComparisonMode}
          incomeCount={incomeCount}
          accountCount={accountCount}
        />
      </div>
    );
  }

  // General error state
  if (error && !connectionError) {
    return (
      <div className="p-4">
        <div className="bg-red-50 text-red-700 p-4 rounded-lg flex items-center mb-6">
          <ExclamationTriangleIcon className="h-6 w-6 mr-2 text-red-500" />
          <div>
            <p className="font-medium">Error loading dashboard: {error}</p>
            <p className="text-sm text-red-600 mt-1">Please try refreshing or loading demo data.</p>
          </div>
          <div className="ml-auto flex gap-2">
            <button 
              onClick={handleRefresh}
              className="text-sm bg-red-100 hover:bg-red-200 text-red-700 px-3 py-1 rounded"
            >
              Try Again
            </button>
            <button 
              onClick={handleUseDemo}
              className="text-sm bg-blue-500 hover:bg-blue-600 text-white px-3 py-1 rounded"
            >
              Use Demo Data
            </button>
          </div>
        </div>
        
        {/* Still render a fallback dashboard */}
        <DashboardContent 
          totalBalance={totalBalance} 
          monthlyIncome={monthlyIncome} 
          monthlyExpenses={monthlyExpenses}
          spendingTrends={spendingTrends}
          categoryBreakdown={categoryBreakdown}
          transactionTrends={transactionTrends}
          incomeTrends={incomeTrends}
          recentActivity={recentActivity}
          isLoading={true} // Force loading state to show skeletons
          isDemo={isDemo}
          lastUpdated={lastUpdated}
          onRefresh={handleRefresh}
          onForceRefresh={handleForceRefresh}
          onUseDemo={handleUseDemo}
          setShowPartnerModal={setShowPartnerModal}
          handleOpenSharingModal={handleOpenSharingModal}
          dateRange={dateRange}
          handleDateRangeChange={handleDateRangeChange}
          comparisonMode={comparisonMode}
          setComparisonMode={setComparisonMode}
          hasPartner={hasPartner}
          partnerName={partnerName}
          dataView={dataView}
          setDataView={setDataView}
          incomeCount={incomeCount}
          accountCount={accountCount}
        />
      </div>
    );
  }

  // Normal view with data
  return (
    <>
      <DashboardContent 
        totalBalance={totalBalance} 
        monthlyIncome={monthlyIncome} 
        monthlyExpenses={monthlyExpenses}
        spendingTrends={spendingTrends}
        categoryBreakdown={categoryBreakdown}
        transactionTrends={transactionTrends}
        incomeTrends={incomeTrends}
        recentActivity={recentActivity}
        isLoading={isLoading}
        isDemo={isDemo}
        lastUpdated={lastUpdated}
        onRefresh={handleRefresh}
        onForceRefresh={handleForceRefresh}
        onUseDemo={handleUseDemo}
        setShowPartnerModal={setShowPartnerModal}
        handleOpenSharingModal={handleOpenSharingModal}
        dateRange={dateRange}
        handleDateRangeChange={handleDateRangeChange}
        comparisonMode={comparisonMode}
        setComparisonMode={setComparisonMode}
        hasPartner={hasPartner}
        partnerName={partnerName}
        dataView={dataView}
        setDataView={setDataView}
        expandedChart={expandedChart}
        setExpandedChart={setExpandedChart}
        incomeCount={incomeCount}
        accountCount={accountCount}
      />
      
      {/* Standardized Sharing Management Modal */}
      <SharingManagementModal
        isOpen={showSharingModal}
        onClose={() => setShowSharingModal(false)}
        user={user}
        onSyncAccounts={handleSyncAccounts}
        onViewActivityLog={handleViewActivityLog}
        onManageSharingSettings={handleManageSharingSettings}
        onViewSharedAccounts={handleViewSharedAccounts}
        onAdvancedSettings={handleAdvancedSettings}
      />
      
      {/* Partner Connection Modal */}
      <PartnerConnectionModal 
        isOpen={showPartnerModal} 
        onClose={() => setShowPartnerModal(false)} 
      />
    </>
  );
};

// Extract dashboard content to a separate component for reuse
const DashboardContent = ({
  totalBalance,
  monthlyIncome,
  monthlyExpenses,
  spendingTrends,
  categoryBreakdown,
  transactionTrends,
  incomeTrends,
  recentActivity,
  isLoading,
  isDemo,
  lastUpdated,
  onRefresh,
  onForceRefresh,
  onUseDemo,
  setShowPartnerModal,
  handleOpenSharingModal,
  dateRange,
  handleDateRangeChange,
  comparisonMode,
  setComparisonMode,
  hasPartner,
  partnerName,
  dataView,
  setDataView,
  expandedChart,
  setExpandedChart,
  incomeCount = 0,
  accountCount = 0
}) => {
  const formatLastUpdated = (date) => {
    if (!date) return '';
    return new Date(date).toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Check if all data is empty (for new users)
  const hasNoData = (
    (!totalBalance || totalBalance.amount === 0) &&
    (!monthlyIncome || monthlyIncome.amount === 0) &&
    (!monthlyExpenses || monthlyExpenses.amount === 0) &&
    (!spendingTrends || !spendingTrends.length || spendingTrends.every(item => !item.amount || item.amount === 0)) &&
    (!categoryBreakdown || !categoryBreakdown.length || categoryBreakdown.every(item => !item.amount || item.amount === 0)) &&
    (!transactionTrends || !transactionTrends.length || transactionTrends.every(item => !item.amount || item.amount === 0)) &&
    (!incomeTrends || !incomeTrends.length || incomeTrends.every(item => !item.amount || item.amount === 0)) &&
    (!recentActivity || !recentActivity.length)
  );

  return (
    <div className="min-h-full overflow-x-hidden">
      <div className="max-w-[1400px] mx-auto p-2 sm:p-6">
        {/* Security Notice */}
        {/* <SecurityNotice /> */}
        
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 mb-4 sm:mb-6">
          <div className="flex items-center gap-2 sm:gap-4">
            <h1 className="text-lg sm:text-xl font-medium text-gray-800">Dashboard</h1>
            {lastUpdated && (
              <span className="text-xs text-gray-500">
                Last updated: {formatLastUpdated(lastUpdated)}
              </span>
            )}
          </div>
          {/* Only show Sharing button if user doesn't have a partner (will be in Views section if they do) */}
          {!hasPartner && (
            <div className="flex items-center gap-2">
              <button
                onClick={handleOpenSharingModal}
                className="flex items-center gap-2 bg-orange-600 hover:bg-orange-700 text-white px-4 py-2 rounded-lg text-sm transition-colors shadow-sm hover:shadow-md"
              >
                <ShareIcon className="h-5 w-5" />
                Sharing
              </button>
            </div>
          )}
        </div>

        {/* View Selector - Only show if user has a partner */}
        {hasPartner && (
          <MobileViewSelector
            title="Views"
            currentView={dataView}
            onViewChange={(view) => {
              console.log(`🔘 View changed to: ${view}`);
              setDataView(view);
            }}
            sharingButton={
              <button
                onClick={handleOpenSharingModal}
                className="rounded-lg bg-orange-600 hover:bg-orange-700 text-white transition-colors flex-shrink-0 flex items-center justify-center"
                style={{ padding: '4.8px' }}
                aria-label="Sharing Settings"
              >
                <ShareIcon className="h-4 w-4" />
              </button>
            }
            views={[
              {
                value: 'personal',
                label: 'My Dashboard',
                icon: <UserIcon className="h-5 w-5" />
              },
              {
                value: 'combined',
                label: 'Our Dashboard',
                icon: <UsersIcon className="h-5 w-5" />
              },
              {
                value: 'partner',
                label: `${partnerName}'s Dashboard`,
                icon: <UserIcon className="h-5 w-5" />
              }
            ]}
          />
        )}

        {/* Demo Data Notice - Only show for demo user */}
        {isDemo && (
          <div className="bg-blue-50 border-l-4 border-blue-500 p-3 sm:p-4 mb-4 sm:mb-6 rounded-md text-xs sm:text-sm">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg className="h-4 w-4 sm:h-5 sm:w-5 text-blue-500" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-2 sm:ml-3">
                <p className="text-blue-700">
                  <strong>Note:</strong> Displaying demo data. To see your actual financial data, add transactions, accounts, or income sources.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Onboarding Checklist */}
        <OnboardingChecklist 
          dashboardData={{
            accounts: recentActivity, // Use as proxy for accounts
            totalBalance: totalBalance,
            monthlyIncome: monthlyIncome, // Pass full monthlyIncome object, not just amount
            incomeTrends: incomeTrends,
            incomeCount: incomeCount, // Pass incomeCount from props
            accountCount: accountCount // Pass accountCount from props
          }}
          onOpenPartnerModal={() => setShowPartnerModal(true)}
        />

        {/* Empty State Notice - For new users with no data */}
        {!isLoading && !isDemo && hasNoData && (
          <div className="bg-gray-50 border border-gray-200 p-6 mb-6 rounded-xl text-center">
            <div className="mx-auto w-16 h-16 bg-violet-100 rounded-full flex items-center justify-center mb-4">
              <svg className="w-8 h-8 text-violet-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
              </svg>
            </div>
            <h3 className="text-lg font-medium text-gray-800 mb-2">Get Started with MnyMny</h3>
            <p className="text-gray-600 mb-6 max-w-md mx-auto">
              Connect your accounts to see your financial data on the dashboard.
            </p>
            <div className="flex justify-center">
              <Link 
                to="/wallet?connect=1" 
                className="text-white px-4 py-2 rounded-lg text-sm transition-colors shadow-md"
                style={{
                  background: `linear-gradient(to right, ${themeColors.primary}, ${themeColors.secondary})`,
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = `linear-gradient(to right, ${themeColors.primaryDark}, ${themeColors.secondaryDark})`;
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = `linear-gradient(to right, ${themeColors.primary}, ${themeColors.secondary})`;
                }}
              >
                Connect Account
              </Link>
            </div>
          </div>
        )}

        {/* Overview Cards */}
        <div className="grid grid-cols-3 gap-1.5 sm:gap-3 mb-4 sm:mb-6">
          {/* Mobile: StatsCard-style, Desktop: Original style */}
          <div className="relative bg-gradient-to-br from-white via-white to-gray-50/95 sm:bg-white shadow-[0_8px_25px_-8px_rgba(0,0,0,0.3)] sm:shadow-sm rounded-xl p-1.5 sm:p-3 border border-white/50 sm:border-0 backdrop-blur-xl sm:backdrop-blur-none transition-all duration-300 hover:translate-y-[-2px] sm:hover:translate-y-0 min-h-[100px] sm:h-[100px] flex flex-col">
            <MonthlyIncomeCard 
              income={monthlyIncome?.amount || 0}
              percentageChange={monthlyIncome?.percentageChange || 0}
              isLoading={isLoading} 
            />
          </div>
          <div className="relative bg-gradient-to-br from-white via-white to-gray-50/95 sm:bg-white shadow-[0_8px_25px_-8px_rgba(0,0,0,0.3)] sm:shadow-sm rounded-xl p-2 sm:p-3 border border-white/50 sm:border-0 backdrop-blur-xl sm:backdrop-blur-none transition-all duration-300 hover:translate-y-[-2px] sm:hover:translate-y-0 min-h-[100px] sm:h-[100px] flex flex-col">
            <TotalBalanceCard 
              balance={totalBalance?.amount || 0} 
              percentageChange={totalBalance?.percentageChange || 0}
              isLoading={isLoading} 
            />
          </div>
          <div className="relative bg-gradient-to-br from-white via-white to-gray-50/95 sm:bg-white shadow-[0_8px_25px_-8px_rgba(0,0,0,0.3)] sm:shadow-sm rounded-xl p-2 sm:p-3 border border-white/50 sm:border-0 backdrop-blur-xl sm:backdrop-blur-none transition-all duration-300 hover:translate-y-[-2px] sm:hover:translate-y-0 min-h-[100px] sm:h-[100px] flex flex-col">
            <MonthlyExpensesCard 
              expenses={monthlyExpenses?.amount || 0}
              percentageChange={monthlyExpenses?.percentageChange || 0}
              isLoading={isLoading} 
            />
          </div>
        </div>

        {/* Charts Section - 2 columns on mobile, 3 on desktop with expand/collapse */}
        <div className="grid grid-cols-2 lg:grid-cols-3 gap-1.5 sm:gap-3 lg:gap-4 mb-4 sm:mb-6">
          {/* Spending by Category */}
          <div className={`min-w-0 transition-all duration-300 ${
            expandedChart === 'category' 
              ? 'col-span-2 lg:col-span-3' 
              : ''
          }`}>
            <div 
              className={`bg-white rounded-xl shadow-sm p-3 lg:p-4 flex flex-col transition-all duration-300 ${
                expandedChart === 'category' 
                  ? 'h-[500px] sm:h-[600px]' 
                  : 'h-[250px] sm:h-[300px] lg:h-[400px]'
              }`}
            >
              <div 
                className="flex items-center justify-center gap-1 mb-1 sm:mb-2 lg:mb-4 cursor-pointer hover:bg-gray-50 rounded px-2 py-1 -mx-2 -mt-2"
                onClick={() => setExpandedChart(expandedChart === 'category' ? null : 'category')}
              >
                <h2 className="text-[10px] sm:text-sm lg:text-base font-medium text-gray-800 text-center">Spending by Category</h2>
                {expandedChart === 'category' ? (
                  <ArrowsPointingInIcon className="h-3 w-3 sm:h-4 sm:w-4 text-gray-500" />
                ) : (
                  <ArrowsPointingOutIcon className="h-3 w-3 sm:h-4 sm:w-4 text-gray-500" />
                )}
              </div>
              <div className="flex-1 min-h-0" onClick={(e) => e.stopPropagation()}>
                <SpendingByCategory 
                  categoryBreakdown={categoryBreakdown || []} 
                  isLoading={isLoading}
                  dateRange={dateRange}
                  isExpanded={expandedChart === 'category'}
                />
              </div>
            </div>
          </div>
          
          {/* Transaction by Account */}
          <div className={`min-w-0 transition-all duration-300 ${
            expandedChart === 'account' 
              ? 'col-span-2 lg:col-span-3' 
              : ''
          }`}>
            <div 
              className={`bg-white rounded-xl shadow-sm p-3 lg:p-4 flex flex-col transition-all duration-300 ${
                expandedChart === 'account' 
                  ? 'h-[500px] sm:h-[600px]' 
                  : 'h-[250px] sm:h-[300px] lg:h-[400px]'
              }`}
            >
              <div 
                className="flex items-center justify-center gap-1 mb-1 sm:mb-2 lg:mb-4 cursor-pointer hover:bg-gray-50 rounded px-2 py-1 -mx-2 -mt-2"
                onClick={() => setExpandedChart(expandedChart === 'account' ? null : 'account')}
              >
                <h2 className="text-[10px] sm:text-sm lg:text-base font-medium text-gray-800 text-center">Transaction by Account</h2>
                {expandedChart === 'account' ? (
                  <ArrowsPointingInIcon className="h-3 w-3 sm:h-4 sm:w-4 text-gray-500" />
                ) : (
                  <ArrowsPointingOutIcon className="h-3 w-3 sm:h-4 sm:w-4 text-gray-500" />
                )}
              </div>
              <div className="flex-1 min-h-0" onClick={(e) => e.stopPropagation()}>
                <TransactionByAccountChart 
                  dataView={dataView} 
                  isExpanded={expandedChart === 'account'}
                />
              </div>
            </div>
          </div>
          
          {/* Spending by Type */}
          <div className={`min-w-0 transition-all duration-300 ${
            expandedChart === 'type' 
              ? 'col-span-2 lg:col-span-3' 
              : ''
          }`}>
            <div 
              className={`bg-white rounded-xl shadow-sm p-3 lg:p-4 flex flex-col transition-all duration-300 ${
                expandedChart === 'type' 
                  ? 'h-[500px] sm:h-[600px]' 
                  : 'h-[250px] sm:h-[300px] lg:h-[400px]'
              }`}
            >
              <div 
                className="flex items-center justify-center gap-1 mb-1 sm:mb-2 lg:mb-4 cursor-pointer hover:bg-gray-50 rounded px-2 py-1 -mx-2 -mt-2"
                onClick={() => setExpandedChart(expandedChart === 'type' ? null : 'type')}
              >
                <h2 className="text-[10px] sm:text-sm lg:text-base font-medium text-gray-800 text-center">Spending by Type</h2>
                {expandedChart === 'type' ? (
                  <ArrowsPointingInIcon className="h-3 w-3 sm:h-4 sm:w-4 text-gray-500" />
                ) : (
                  <ArrowsPointingOutIcon className="h-3 w-3 sm:h-4 sm:w-4 text-gray-500" />
                )}
              </div>
              <div className="flex-1 min-h-0" onClick={(e) => e.stopPropagation()}>
                <SpendingByTypeChart 
                  dataView={dataView} 
                  isExpanded={expandedChart === 'type'}
                />
              </div>
            </div>
          </div>
          
          {/* Recent Highest Spend - Mobile: part of chart cards grid, Desktop: hidden here */}
          <div className={`min-w-0 transition-all duration-300 lg:hidden ${
            expandedChart === 'highest' 
              ? 'col-span-2' 
              : ''
          }`}>
            <div 
              className={`bg-white rounded-xl shadow-sm p-3 lg:p-4 flex flex-col transition-all duration-300 ${
                expandedChart === 'highest' 
                  ? 'h-[500px] sm:h-[600px]' 
                  : 'h-[250px] sm:h-[300px] lg:h-[400px]'
              }`}
            >
              <div className="flex justify-between items-center mb-1 sm:mb-2 lg:mb-4">
                <div 
                  className="flex items-center justify-center gap-1 flex-1 cursor-pointer hover:bg-gray-50 rounded px-2 py-1 -mx-2 -mt-2"
                  onClick={() => setExpandedChart(expandedChart === 'highest' ? null : 'highest')}
                >
                  <h2 className="text-[10px] sm:text-sm lg:text-base font-medium text-gray-800 text-center">Recent Highest Spend</h2>
                  {expandedChart === 'highest' ? (
                    <ArrowsPointingInIcon className="h-3 w-3 sm:h-4 sm:w-4 text-gray-500" />
                  ) : (
                    <ArrowsPointingOutIcon className="h-3 w-3 sm:h-4 sm:w-4 text-gray-500" />
                  )}
                </div>
                <Link 
                  to="/transactions" 
                  className="text-[8px] sm:text-xs lg:text-sm text-violet-500 hover:text-violet-600 transition-colors ml-2"
                  onClick={(e) => e.stopPropagation()}
                >
                  View All
                </Link>
              </div>
              <div className="flex-1 min-h-0 overflow-y-auto" onClick={(e) => e.stopPropagation()}>
                <RecentActivity 
                  recentActivity={recentActivity || []} 
                  isLoading={isLoading}
                  showHighestOnly={true}
                  isExpanded={expandedChart === 'highest'}
                />
              </div>
            </div>
          </div>
        </div>

        {/* Spending Trend and Recent Highest Spend Section - 2 columns on desktop only */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-3 sm:gap-4 mb-4 sm:mb-6">
          <div className="lg:col-span-2 min-w-0">
            <div className="bg-white rounded-xl shadow-sm p-3 sm:p-4 h-[300px] sm:h-[400px] flex flex-col relative overflow-hidden">
              <h2 className="text-sm sm:text-base font-medium text-gray-800 mb-2 sm:mb-4 z-10">Spending Trend</h2>
              <div className="flex-1 min-h-0 relative z-10">
                <SpendingTrendChart 
                  spendingTrends={spendingTrends || []} 
                  incomeTrends={incomeTrends || []}
                  isLoading={isLoading} 
                />
              </div>
              <div className="absolute inset-0 bg-gradient-to-br from-violet-50/30 to-blue-50/30 backdrop-blur-sm"></div>
            </div>
          </div>
          {/* Recent Highest Spend - Desktop: part of Spending Trend section, Mobile: hidden here */}
          <div className="hidden lg:block min-w-0">
            <div 
              className={`bg-white rounded-xl shadow-sm p-3 lg:p-4 flex flex-col transition-all duration-300 ${
                expandedChart === 'highest' 
                  ? 'h-[500px] sm:h-[600px]' 
                  : 'h-[300px] sm:h-[400px]'
              }`}
            >
              <div className="flex justify-between items-center mb-1 sm:mb-2 lg:mb-4">
                <div 
                  className="flex items-center justify-center gap-1 flex-1 cursor-pointer hover:bg-gray-50 rounded px-2 py-1 -mx-2 -mt-2"
                  onClick={() => setExpandedChart(expandedChart === 'highest' ? null : 'highest')}
                >
                  <h2 className="text-[10px] sm:text-sm lg:text-base font-medium text-gray-800 text-center">Recent Highest Spend</h2>
                  {expandedChart === 'highest' ? (
                    <ArrowsPointingInIcon className="h-3 w-3 sm:h-4 sm:w-4 text-gray-500" />
                  ) : (
                    <ArrowsPointingOutIcon className="h-3 w-3 sm:h-4 sm:w-4 text-gray-500" />
                  )}
                </div>
                <Link 
                  to="/transactions" 
                  className="text-[8px] sm:text-xs lg:text-sm text-violet-500 hover:text-violet-600 transition-colors ml-2"
                  onClick={(e) => e.stopPropagation()}
                >
                  View All
                </Link>
              </div>
              <div className="flex-1 min-h-0 overflow-y-auto" onClick={(e) => e.stopPropagation()}>
                <RecentActivity 
                  recentActivity={recentActivity || []} 
                  isLoading={isLoading}
                  showHighestOnly={true}
                  isExpanded={expandedChart === 'highest'}
                />
              </div>
            </div>
          </div>
        </div>

        {/* Income Trends and Insights Section */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-3 sm:gap-4 mb-4 sm:mb-6">
          <div className="lg:col-span-2 min-w-0">
            <div className="bg-white rounded-xl shadow-sm p-3 sm:p-4 h-[300px] sm:h-[400px] flex flex-col">
              <h2 className="text-sm sm:text-base font-medium text-gray-800 mb-2 sm:mb-4">Net Income vs Spending</h2>
              <div className="flex-1 min-h-0">
                <IncomeVsSpendingChart 
                  incomeTrends={incomeTrends || []} 
                  spendingTrends={spendingTrends || []}
                  isLoading={isLoading} 
                />
              </div>
            </div>
          </div>
          <div className="min-w-0">
            <div className="h-[300px] sm:h-[400px] overflow-y-auto">
              <InsightsPanel 
                dataView={dataView}
              />
            </div>
          </div>
        </div>

        {/* Cumulative Wealth Projection Section */}
        <div className="mb-4 sm:mb-6">
          <div className="bg-white rounded-xl shadow-sm p-3 sm:p-4 h-[350px] sm:h-[450px] flex flex-col">
            <h2 className="text-sm sm:text-base font-medium text-gray-800 mb-2 sm:mb-4">
              Cumulative Wealth Projection
              <span className="hidden sm:inline text-xs text-gray-500 ml-2 font-normal">
                Running totals - each month adds to the previous
              </span>
              <span className="sm:hidden text-xs text-gray-500 ml-2 font-normal">
                Running totals
              </span>
            </h2>
            <div className="flex-1 min-h-0">
              <CumulativeWealthChart 
                incomeTrends={incomeTrends || []} 
                spendingTrends={spendingTrends || []}
                projectedMonthlyIncome={monthlyIncome?.amount || 0}
                isLoading={isLoading} 
              />
            </div>
          </div>
        </div>

        {/* Monthly Income vs Spending Section */}
        <div className="mb-4 sm:mb-6">
          <div className="bg-white rounded-xl shadow-sm p-3 sm:p-4 h-[350px] sm:h-[450px] flex flex-col">
            <h2 className="text-sm sm:text-base font-medium text-gray-800 mb-2 sm:mb-4">
              Monthly Income vs Spending
              <span className="text-xs text-gray-500 ml-2 font-normal">
                Individual months - income (positive) vs spending (negative)
              </span>
            </h2>
            <div className="flex-1 min-h-0">
              <MonthlyIncomeSpendingChart 
                incomeTrends={incomeTrends || []} 
                spendingTrends={spendingTrends || []}
                recentActivity={recentActivity || []}
                projectedMonthlyIncome={monthlyIncome?.amount || 0}
                isLoading={isLoading} 
              />
            </div>
          </div>
        </div>

        {/* Remove the savings and debt trends section entirely */}
      </div>
    </div>
  );
};

export default Dashboard; 